import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Autocomplete from "@mui/material/Autocomplete";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import PlayerSearch from "components/admin/PlayerSearch";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";

// Khớp TaskCalculator.TASK_TYPE_* phía máy chủ.
const TYPE_MAIN = 0;
const TYPE_CLAN = 2;
const TYPE_DAILY = 3;
const TYPE_RETIRED = 99;

/** Máy chủ ghi "[0] Diệt quái ×10 — …"; mã ở đầu chỉ có ích cho kỹ thuật. */
const plain = (text) => String(text || "").replace(/^\[\d+\]\s*/, "");

const taskName = (tasks, id) => tasks?.[id]?.name || `Nhiệm vụ #${id}`;

/** Sửa tiến độ từng dòng yêu cầu của một nhiệm vụ người chơi đang làm. */
function ProgressDialog({ userId, row, onClose, onSaved }) {
  const [run, busy] = useRunner();
  const [values, setValues] = useState((row.progress || []).map(String));
  const lines = row.taskText || [];
  const target = (i) => row.target?.[i] ?? 0;
  // Máy chủ chặn trên bằng mục tiêu, nên hiển thị đúng con số sẽ được lưu.
  const clamp = (i, v) => Math.max(0, Math.min(Number(v) || 0, target(i) || Infinity));

  const changes = lines
    .map((text, i) => ({ text, from: row.progress?.[i] ?? 0, to: clamp(i, values[i]) }))
    .filter((c) => c.from !== c.to);

  const setAt = (i, v) => {
    const copy = [...values];
    copy[i] = String(v);
    setValues(copy);
  };

  const submit = async () => {
    const res = await run(
      () =>
        api.post(`/api/admin/tasks/players/${userId}/progress`, {
          taskId: row.taskId,
          progress: lines.map((_, i) => clamp(i, values[i])),
        }),
      "Đã lưu tiến độ"
    );
    if (res.ok) {
      onSaved();
      onClose();
    }
    return res;
  };

  return (
    <ActionDialog
      open
      maxWidth="sm"
      title={`Chỉnh tiến độ: ${row.name || `nhiệm vụ #${row.taskId}`}`}
      submitText="Lưu tiến độ"
      busy={busy}
      disabled={changes.length === 0}
      onClose={onClose}
      onSubmit={submit}
    >
      {row.stale && (
        <Alert severity="warning">
          Nhiệm vụ này đã được sửa sau khi người chơi nhận. Người chơi vẫn làm theo bản cũ dưới đây.
        </Alert>
      )}
      <Grid container spacing={2}>
        {lines.map((text, i) => (
          /* eslint-disable-next-line react/no-array-index-key */
          <Grid item xs={12} key={i}>
            <MDTypography variant="button" display="block" mb={0.5}>
              {plain(text)}
            </MDTypography>
            <MDBox display="flex" gap={1} alignItems="center">
              <MDInput
                size="small"
                type="number"
                inputProps={{ min: 0, max: target(i) || undefined }}
                label={`Đã đạt (cần ${target(i)})`}
                value={values[i] ?? "0"}
                onChange={(e) => setAt(i, e.target.value)}
                sx={{ flex: 1 }}
              />
              <MDButton
                size="small"
                variant="outlined"
                color="success"
                disabled={clamp(i, values[i]) >= target(i)}
                onClick={() => setAt(i, target(i))}
              >
                Đặt đủ
              </MDButton>
            </MDBox>
          </Grid>
        ))}
      </Grid>
      {changes.length > 0 ? (
        <Alert severity="info">
          Sẽ đổi:
          {changes.map((c) => (
            <div key={c.text}>
              • {plain(c.text)}: {c.from} → <b>{c.to}</b>
            </div>
          ))}
        </Alert>
      ) : (
        <MDTypography variant="caption" color="text">
          Chưa đổi số nào.
        </MDTypography>
      )}
      <MDTypography variant="caption" color="text">
        Số lớn hơn mục tiêu sẽ được hạ về bằng mục tiêu. Đặt đủ mọi dòng thì người chơi tới NPC trả
        nhiệm vụ và nhận quà như bình thường.
      </MDTypography>
    </ActionDialog>
  );
}

ProgressDialog.propTypes = {
  userId: PropTypes.number.isRequired,
  row: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

/** Một nhóm chip nhiệm vụ đã xong, bấm x để cho làm lại. */
function DoneGroup({ title, ids, tasks, emptyText, onRevoke, color }) {
  return (
    <MDBox mb={2}>
      <MDTypography variant="button" fontWeight="medium" display="block" mb={0.5}>
        {title} ({ids.length})
      </MDTypography>
      {ids.length === 0 ? (
        <MDTypography variant="caption" color="text">
          {emptyText}
        </MDTypography>
      ) : (
        <MDBox display="flex" gap={0.5} flexWrap="wrap">
          {ids.map((id) => (
            <Tooltip
              key={id}
              title={`Mã nhiệm vụ #${id}${onRevoke ? " · bấm x để cho làm lại" : ""}`}
            >
              <Chip
                size="small"
                color={color}
                variant={color ? "filled" : "outlined"}
                label={taskName(tasks, id)}
                onDelete={onRevoke ? () => onRevoke(id) : undefined}
              />
            </Tooltip>
          ))}
        </MDBox>
      )}
    </MDBox>
  );
}

DoneGroup.propTypes = {
  title: PropTypes.string.isRequired,
  ids: PropTypes.arrayOf(PropTypes.number).isRequired,
  tasks: PropTypes.object,
  emptyText: PropTypes.string.isRequired,
  onRevoke: PropTypes.func,
  color: PropTypes.string,
};

/** Tra và sửa tiến độ nhiệm vụ của một người chơi. */
function PlayerProgress({ tasks }) {
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const [who, setWho] = useState(null);
  const [editing, setEditing] = useState(null);
  const [give, setGive] = useState(null);
  const canEdit = useCan(PERMISSIONS.TASK_EDIT);
  const userId = who?.userId;

  const data = useFetch(
    () => (userId ? api.get(`/api/admin/tasks/players/${userId}`) : Promise.resolve(null)),
    [userId]
  );
  const p = data.data;
  const whoName = who?.name || `tài khoản ${userId}`;
  const options = useMemo(
    () =>
      Object.entries(tasks || {})
        .map(([id, t]) => ({ id: Number(id), ...t }))
        .filter((t) => t.type !== TYPE_RETIRED)
        .sort((a, b) => a.id - b.id),
    [tasks]
  );

  /** Các nhiệm vụ đang cần `id` làm điều kiện — để nói rõ hậu quả khi cho làm lại. */
  const dependents = (id) =>
    options.filter((t) => (t.taskNeed || []).includes(id)).map((t) => t.name);

  const act = async (taskId, action, message) => {
    const res = await run(
      () => api.post(`/api/admin/tasks/players/${userId}/action`, { taskId, action }),
      message
    );
    if (res.ok) data.reload();
    return res.ok;
  };

  const complete = async (row) => {
    const type = tasks?.[row.taskId]?.type;
    const ok = await confirm({
      title: "Đánh dấu nhiệm vụ đã xong",
      message: [
        `"${row.name}" của ${whoName} sẽ chuyển sang đã xong.`,
        "",
        "• Người chơi KHÔNG nhận quà của nhiệm vụ. Muốn bù quà thì gửi qua trang Thư & quà.",
        type === TYPE_MAIN && "• Mở các nhiệm vụ chính tuyến phía sau đang chờ nhiệm vụ này.",
        (type === TYPE_DAILY || type === TYPE_CLAN) &&
          "• Hôm nay người chơi không nhận lại nhiệm vụ này nữa.",
        "",
        'Nếu người chơi chỉ bị kẹt và muốn nhận quà: dùng "Chỉnh tiến độ" → Đặt đủ, rồi họ tự trả ở NPC.',
      ]
        .filter((l) => l !== false)
        .join("\n"),
      color: "warning",
      confirmText: "Đánh dấu xong, không quà",
    });
    if (ok) act(row.taskId, "complete", "Đã đánh dấu xong");
  };

  const drop = async (row) => {
    const ok = await confirm({
      title: "Bỏ nhiệm vụ đang làm",
      message: [
        `Bỏ "${row.name}" khỏi danh sách đang làm của ${whoName}?`,
        "",
        "• Tiến độ đang có sẽ mất.",
        "• Người chơi có thể nhận lại từ NPC giao (nếu còn đủ điều kiện) và làm lại từ đầu.",
      ].join("\n"),
      color: "error",
      confirmText: "Bỏ nhiệm vụ",
    });
    if (ok) act(row.taskId, "drop", "Đã bỏ nhiệm vụ");
  };

  const revoke = async (id, group) => {
    const after = group === "main" ? dependents(id) : [];
    const ok = await confirm({
      title: "Cho làm lại nhiệm vụ",
      message: [
        `Bỏ "${taskName(tasks, id)}" khỏi danh sách đã xong của ${whoName}?`,
        "",
        group === "main"
          ? "• Người chơi phải làm lại nhiệm vụ này (nhận lại ở NPC giao)."
          : "• Người chơi được nhận và làm lại nhiệm vụ này ngay hôm nay.",
        after.length > 0 &&
          `• Các nhiệm vụ cần nó sẽ không nhận được cho tới khi làm lại: ${after.join(", ")}.`,
        "• Quà đã nhận trước đó không bị thu hồi.",
      ]
        .filter((l) => l !== false)
        .join("\n"),
      color: "warning",
      confirmText: "Cho làm lại",
    });
    if (ok) act(id, "revoke", "Đã cho làm lại");
  };

  const giveBlocker =
    give && p
      ? (p.tasking || []).includes(give.id)
        ? "Người chơi đang làm nhiệm vụ này rồi."
        : (p.wasTask || []).includes(give.id) || (p.dailyTasked || []).includes(give.id)
        ? 'Người chơi đã xong nhiệm vụ này — bấm x ở mục "Đã xong" bên dưới trước.'
        : null
      : null;

  const giveTask = async () => {
    const ok = await confirm({
      title: "Giao nhiệm vụ",
      message: [
        `Giao "${give.name}" cho ${whoName}?`,
        "",
        "• Bỏ qua mọi điều kiện mở (nhiệm vụ cần xong trước, cấp pet).",
        "• Người chơi bắt đầu từ 0 và nhận quà khi trả ở NPC như bình thường.",
      ].join("\n"),
      confirmText: "Giao",
    });
    if (ok && (await act(give.id, "give", "Đã giao nhiệm vụ"))) setGive(null);
  };

  const doingColumns = [
    {
      key: "name",
      label: "Nhiệm vụ",
      wrap: true,
      render: (row) => (
        <MDBox lineHeight={1.2}>
          <MDTypography variant="button" fontWeight="medium" display="block">
            {row.name || "(không rõ tên)"}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            #{row.taskId}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      key: "progress",
      label: "Tiến độ",
      wrap: true,
      render: (row) => (
        <MDBox display="flex" flexDirection="column" gap={0.5}>
          {(row.taskText || []).map((text, i) => (
            /* eslint-disable-next-line react/no-array-index-key */
            <MDBox key={i} display="flex" alignItems="center" gap={1}>
              <Chip
                size="small"
                color={row.progress?.[i] >= row.target?.[i] ? "success" : "default"}
                variant={row.progress?.[i] >= row.target?.[i] ? "filled" : "outlined"}
                label={`${row.progress?.[i] ?? 0} / ${row.target?.[i] ?? "?"}`}
              />
              <MDTypography variant="caption">{plain(text)}</MDTypography>
            </MDBox>
          ))}
          {row.stale && (
            <Tooltip title="Nhiệm vụ đã được sửa sau khi người chơi nhận; người chơi làm theo bản cũ">
              <Chip
                size="small"
                color="warning"
                label="Làm theo bản cũ"
                sx={{ width: "fit-content" }}
              />
            </Tooltip>
          )}
        </MDBox>
      ),
    },
    canEdit && {
      key: "actions",
      label: "",
      align: "right",
      render: (row) => (
        <MDBox
          display="flex"
          gap={1}
          justifyContent="flex-end"
          flexDirection={{ xs: "column", lg: "row" }}
        >
          <MDButton size="small" variant="outlined" color="info" onClick={() => setEditing(row)}>
            Chỉnh tiến độ
          </MDButton>
          <MDButton
            size="small"
            variant="outlined"
            color="warning"
            disabled={busy}
            onClick={() => complete(row)}
          >
            Đánh dấu xong
          </MDButton>
          <MDButton
            size="small"
            variant="outlined"
            color="error"
            disabled={busy}
            onClick={() => drop(row)}
          >
            Bỏ
          </MDButton>
        </MDBox>
      ),
    },
  ].filter(Boolean);

  return (
    <MDBox>
      <PageCard
        title="Tra người chơi"
        subtitle="Khách báo kẹt nhiệm vụ: xem họ đang làm gì, chỉnh tiến độ, giao lại hoặc cho làm lại"
        color="info"
      >
        <PlayerSearch onPick={setWho} />
        {who && (
          <MDBox mt={2} display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <MDTypography variant="button" fontWeight="medium">
              {who.name ? <Link to={`/players/${userId}`}>{who.name}</Link> : whoName}
            </MDTypography>
            {who.username && (
              <MDTypography variant="caption" color="text">
                tài khoản {who.username}
              </MDTypography>
            )}
            {p && (
              <Chip
                size="small"
                color={p.online ? "success" : "default"}
                label={p.online ? "Đang online" : "Offline"}
              />
            )}
            {p && (
              <MDTypography variant="caption" color="text">
                {p.online
                  ? "Thay đổi có hiệu lực ngay trong game."
                  : "Thay đổi được lưu, người chơi thấy khi đăng nhập lại."}
              </MDTypography>
            )}
          </MDBox>
        )}
        {data.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {data.error}
          </Alert>
        )}
      </PageCard>

      {p && (
        <MDBox mt={3}>
          <PageCard
            title={`Đang làm (${(p.doing || []).length})`}
            subtitle="Xanh: dòng yêu cầu đã đủ"
            color="warning"
            noPadding
          >
            <AdminTable
              columns={doingColumns}
              rows={p.doing || []}
              rowKey="taskId"
              loading={data.loading}
              emptyText="Người chơi không làm nhiệm vụ nào"
            />
          </PageCard>

          {canEdit && (
            <MDBox mt={3}>
              <PageCard
                title="Giao thêm nhiệm vụ"
                subtitle="Dùng khi người chơi mất nhiệm vụ do lỗi hoặc cần bỏ qua điều kiện mở"
                color="success"
              >
                <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <Autocomplete
                    size="small"
                    options={options}
                    value={give}
                    onChange={(e, v) => setGive(v)}
                    getOptionLabel={(t) => t.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    groupBy={(t) => t.typeName}
                    renderOption={(props, t) => (
                      /* eslint-disable-next-line react/jsx-props-no-spreading */
                      <li {...props} key={t.id}>
                        {t.name}&nbsp;
                        <MDTypography variant="caption" color="text">
                          #{t.id}
                        </MDTypography>
                      </li>
                    )}
                    renderInput={(params) => <MDInput {...params} label="Chọn nhiệm vụ (gõ tên)" />}
                    sx={{ minWidth: 300, flex: { xs: 1, md: "0 1 380px" } }}
                  />
                  <MDButton
                    variant="gradient"
                    color="success"
                    size="small"
                    disabled={busy || !give || Boolean(giveBlocker)}
                    onClick={giveTask}
                  >
                    <Icon>add_task</Icon>&nbsp;Giao
                  </MDButton>
                </MDBox>
                {giveBlocker && (
                  <MDTypography variant="caption" color="error" display="block" mt={1}>
                    {giveBlocker}
                  </MDTypography>
                )}
              </PageCard>
            </MDBox>
          )}

          <MDBox mt={3}>
            <PageCard
              title="Đã xong"
              subtitle={canEdit ? "Bấm x trên một nhiệm vụ để cho người chơi làm lại" : undefined}
              color="dark"
            >
              <DoneGroup
                title="Chính tuyến"
                ids={p.wasTask || []}
                tasks={tasks}
                emptyText="Chưa xong nhiệm vụ chính tuyến nào"
                onRevoke={canEdit ? (id) => revoke(id, "main") : undefined}
              />
              <DoneGroup
                title="Hằng ngày — đã làm hôm nay"
                ids={p.dailyTasked || []}
                tasks={tasks}
                color="info"
                emptyText="Hôm nay chưa làm nhiệm vụ hằng ngày nào"
                onRevoke={canEdit ? (id) => revoke(id, "daily") : undefined}
              />
              <DoneGroup
                title="Bang hội — đã làm hôm nay"
                ids={p.clanTasked || []}
                tasks={tasks}
                color="info"
                emptyText="Hôm nay chưa làm nhiệm vụ bang nào"
                onRevoke={canEdit ? (id) => revoke(id, "clan") : undefined}
              />
            </PageCard>
          </MDBox>
        </MDBox>
      )}

      {editing && (
        <ProgressDialog
          userId={userId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={data.reload}
        />
      )}
    </MDBox>
  );
}

PlayerProgress.propTypes = {
  /** map taskId -> { name, type, typeName, taskNeed } để hiện tên và nói rõ hậu quả. */
  tasks: PropTypes.object,
};

export default PlayerProgress;
